import { useId, type HTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * One band of settings, under its own heading.
 *
 * Nothing had drifted when this moved into `ui/`, because there was only ever
 * one copy of it. The scar is the other kind. This lived inside
 * `SettingsList.tsx`, a 425-line feature file, and `/dev/lookbook` imported it
 * out of there to demo it — so the page whose whole job is to say "these are
 * the shapes every setting is made of" was reaching into a screen to make the
 * claim. A primitive that lives inside its first caller is a primitive nothing
 * else can find, and `brain/brand/BRAND.md` had already promised this one was
 * "defined once in its own component under `ui/`" while it was not.
 *
 * It carries two things a caller would otherwise write out, and one of them is
 * the kind that gets forgotten. The band is a named region, so a screen reader
 * jumps between Sound, Table and Play instead of walking every row. And the
 * thin rule between rows is a descendant rule on the `<label>`s the rows render:
 * there is no element in the caller's hands to put a class on, so it cannot be
 * the caller's job.
 *
 * **The name comes from the heading, not from a second copy of it.** This used
 * to render `aria-label={title}` *and* the visible `<h2>` saying the same
 * words, which puts the same string into the accessible tree twice and makes
 * the two able to disagree the moment anyone edits one. `ui/Panel.tsx` had
 * already settled this for the rest of the product: it passes `aria-labelledby`
 * through, five of its seven call sites point it at their own heading, and its
 * header argues the case. `Section` was the outlier, so it now points
 * `aria-labelledby` at the `<h2>` it is already drawing. The role and the
 * accessible name are unchanged — `region`, named "Sound" — which is what
 * `e2e/tests/settings.spec.ts` navigates by.
 */

export interface SectionProps extends Omit<
  HTMLAttributes<HTMLElement>,
  'className' | 'style' | 'children' | 'title'
> {
  /**
   * The heading, and the region's accessible name. Already translated.
   *
   * `title` is omitted from the passthrough above rather than renamed: HTML's
   * own `title` is a tooltip, and a section that showed its heading again on
   * hover would be a mistake nobody meant to make.
   */
  readonly title: string;
  /** Layout and spacing only. The heading and the row rule are not yours. */
  readonly className?: string | undefined;
  readonly children: ReactNode;
}

/*
 * A band is a chip panel: white face, 2px ink stroke, big radius, and the hard
 * offset shadow that says this is a thing sitting on the page rather than a
 * region of it. It is the same chip a button is, at panel size, which is what
 * makes a settings screen look like one object instead of a list of them.
 *
 * The rule between control rows is `label + label` rather than `divide-y`
 * because a section holds a mix — control rows render as `<label>`, and the
 * `<div>`s between them are not rows and must not be ruled off. It is drawn in
 * `gray-300`, the one hairline value in the scale: a divider inside a chip only
 * has to say "next row", and anything heavier competes with the 2px stroke
 * around the panel.
 */
const SECTION =
  'flex flex-col gap-0.5 rounded-3xl border-2 border-ink bg-ground-light p-5 shadow-sm shadow-ink section-panel';

export function Section({ title, className, children, ...rest }: SectionProps): React.JSX.Element {
  const heading = useId();
  return (
    <section className={cx(SECTION, className)} aria-labelledby={heading} {...rest}>
      <h2
        className="mb-2 text-xs font-extrabold tracking-widest text-gray-700 uppercase"
        id={heading}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}
